import type { ElevationSourceQualification } from '../../types/elevationSourceQualification';
import { elevationQualificationText, resolveElevationQualification } from '../../lib/elevation/sourceQualification';
/**
 * Synthetic `site-profile` provider for the SED top-up dispatcher.
 *
 * Content-Source Fixes Prompt 5 plugged the gap in
 * `SECTION_TO_PROVIDER_ID` for sections that had no provider entry —
 * including `siteProfile`. The real reconstruction lives in
 * `landReconstruction.ts` and runs as part of the orchestrator's
 * full site-evidence compile, where `siteBoundary` is in scope. The
 * provider here is a thin wrapper that satisfies the
 * `DataProvider<SiteProfileEvidence>` shape so `topUpSiteSed` can
 * recognise the section.
 *
 * Given only (lat, lng) — the dispatcher's signature — this provider
 * returns a single-point fallback: zero acreage, zero perimeter,
 * the centroid elevation via the proxy. That's enough for the SED
 * `siteProfile` slot to flip from `unavailable` to `verified-from-api`
 * with a partial-data note; richer geometry is recomputed by the
 * orchestrator when the site has a real boundary.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { gridCellKey, CACHE_TTL, fetchWithTimeout, checkCoordValidity } from './types';
import type { SiteProfileEvidence } from '../../types/siteEvidence';
import { refdataElevationPoints, tryRefdata, isPrivateRefdataAcceptanceDataset } from '../refdata/refdataClient';

/**
 * Bucketed outcome of a single-point EPQS lookup — drives the
 * `[provider:elevation]` telemetry breadcrumb that Cloud Run logs grep.
 *
 * Mirrors the buckets used in `lib/fetchPolygonElevations.ts` so EL1
 * diagnostics line up across the polygon-sample path AND the synthetic
 * provider's single-point fallback.
 */
type ElevationOutcomeBucket =
  | 'ok'
  | 'parse-error'
  | 'network-error'
  | 'non-200'
  | 'no-value';

function logElevationTelemetry(
  phase: 'request' | 'response',
  payload: Record<string, unknown>,
): void {
  // eslint-disable-next-line no-console
  console.info(`[provider:elevation] ${phase}`, payload);
}

/**
 * Single-point USGS EPQS elevation lookup.
 *
 * Units: returns metres ASL on success (EPQS query is `units=Meters`),
 * `null` on any failure mode. Never throws.
 */
async function fetchPointElevation(
  lat: number,
  lng: number,
  signal?: AbortSignal,
): Promise<{ value: number | null; sourceQualification?: ElevationSourceQualification }> {
  // EL4 — reject garbage coordinates BEFORE hitting the upstream so we
  // never spend budget on (0, 0) / NaN / out-of-range queries. Emits a
  // dedicated telemetry breadcrumb so Cloud Run logs can grep
  // `[provider:elevation] … bucket: invalid-coords` to count rejected
  // dispatches.
  const validity = checkCoordValidity(lat, lng);
  if (!validity.ok) {
    logElevationTelemetry('response', {
      caller: 'siteProfile.fetchPointElevation',
      bucket: 'invalid-coords',
      reason: validity.reason,
      lat,
      lng,
    });
    return { value: null };
  }
  // RD-1 (C6) — local-first 3DEP snapshot point sample. A nodata/null for
  // this point (wrapper keeps it from negative-caching the dataset) or an
  // unavailable dataset falls through to the live EPQS request below.
  const localPoint = await tryRefdata('usgs-3dep', async () => {
    const res = await refdataElevationPoints([{ lat, lng }]);
    if (!res) return null;
    return { value: res.elevations[0] ?? null, sourceQualification: resolveElevationQualification(res.sourceQualification, res.version) };
  });
  if (localPoint && localPoint.value != null && Number.isFinite(localPoint.value)) {
    logElevationTelemetry('response', {
      caller: 'siteProfile.fetchPointElevation',
      bucket: 'ok' as ElevationOutcomeBucket,
      elevation: localPoint.value,
    });
    return localPoint;
  }
  if (isPrivateRefdataAcceptanceDataset('usgs-3dep')) return { value: null };

  logElevationTelemetry('request', {
    caller: 'siteProfile.fetchPointElevation',
    lat,
    lng,
    upstream: 'epqs.nationalmap.gov',
  });
  try {
    const url = `https://epqs.nationalmap.gov/v1/json?x=${lng}&y=${lat}&units=Meters&wkid=4326&includeDate=false`;
    const res = await fetchWithTimeout(url, 8_000, signal);
    if (!res.ok) {
      logElevationTelemetry('response', {
        caller: 'siteProfile.fetchPointElevation',
        bucket: 'non-200' as ElevationOutcomeBucket,
        httpStatus: res.status,
      });
      return { value: null };
    }
    let json: { value?: number | string } = {};
    try {
      json = await res.json();
    } catch (e) {
      logElevationTelemetry('response', {
        caller: 'siteProfile.fetchPointElevation',
        bucket: 'parse-error' as ElevationOutcomeBucket,
        error: (e as Error)?.message ?? 'unknown',
      });
      return { value: null };
    }
    const v = Number(json?.value);
    if (!Number.isFinite(v)) {
      logElevationTelemetry('response', {
        caller: 'siteProfile.fetchPointElevation',
        bucket: 'no-value' as ElevationOutcomeBucket,
        rawValue: json?.value ?? null,
      });
      return { value: null };
    }
    logElevationTelemetry('response', {
      caller: 'siteProfile.fetchPointElevation',
      bucket: 'ok' as ElevationOutcomeBucket,
      elevation: v,
    });
    return { value: v };
  } catch (e) {
    logElevationTelemetry('response', {
      caller: 'siteProfile.fetchPointElevation',
      bucket: 'network-error' as ElevationOutcomeBucket,
      error: (e as Error)?.message ?? 'unknown',
    });
    return { value: null };
  }
}

/**
 * Cache contract version — see refdataCacheContract.test.ts. siteProfile
 * consults refdata for elevation, so a snapshot change must invalidate its
 * cached answers; the location-only default key cannot express that.
 */
const SITE_PROFILE_CACHE_CONTRACT_VERSION = 'v3-source-qualified-elevation';

export const siteProfileProvider: DataProvider<SiteProfileEvidence> = {
  id: 'site-profile',
  name: 'Site Profile (boundary derivations)',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,

  cacheKey: ({ lat, lng }) => `${SITE_PROFILE_CACHE_CONTRACT_VERSION}_${gridCellKey(lat, lng)}`,

  async fetch(
    lat: number,
    lng: number,
    _apiKey?: string,
    geo?: ProviderGeoContext,
  ): Promise<ProviderResult<SiteProfileEvidence>> {
    // CA3: `(lat, lng)` is now the boundary centroid when a polygon is drawn
    // (`site-profile` is in CENTROID_QUERY_PROVIDER_IDS) — which makes the
    // "centroid-only" label below literally true — else the geocoded address
    // for legacy sites. When the site has a real boundary in scope the
    // orchestrator overlays full geometry from `site.siteBoundary` anyway.
    const elevationResult = await fetchPointElevation(lat, lng, geo?.signal);
    const elevation = elevationResult.value;
    const now = new Date().toISOString();
    // EL2: `elevation ?? 0` is the original-bug clobber. Now that the
    // SED schema accepts `number | null`, propagate null through so
    // downstream renderers + LLM prompts can say "elevation unavailable"
    // instead of "0 m ASL" (which is indistinguishable from a real
    // coastal site at sea level).
    // Units: metres ASL.
    const data: SiteProfileEvidence = {
      acres: 0,
      perimeterKm: 0,
      elevationMin: elevation,
      elevationMax: elevation,
      elevationAvg: elevation,
      ...(elevationResult.sourceQualification ? { elevationSourceQualification: elevationResult.sourceQualification } : {}),
      // EL3 — single-point sample → stdev is undefined-by-construction.
      // pointsSampled is 1 on success, 0 on failure (lookup returned
      // null). The orchestrator overlays full multi-point geometry
      // from site.siteBoundary when present, so this fallback rarely
      // hits the report layer.
      elevationStdev: null,
      elevationPointsSampled: elevation !== null ? 1 : 0,
      slopePercent: 0,
    };
    return {
      data,
      sources: [{
        field: 'siteProfile',
        source: elevation !== null
          ? `Site profile (centroid-only): elevation ${elevation.toFixed(1)}m at (${lat.toFixed(4)}, ${lng.toFixed(4)}). Boundary geometry recomputed from site.siteBoundary when present.`
          : 'Site profile (centroid-only): elevation unavailable; boundary geometry recomputed from site.siteBoundary when present.',
        retrievedAt: now,
        ...(elevationResult.sourceQualification ? { subItems: [{ topic: 'Elevation source qualification', detail: elevationQualificationText(elevationResult.sourceQualification) }] } : {}),
      }],
    };
  },
};
